import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import PromoCodeTable from './components/PromoCodeTable';
import PromoCodeFormModal from './components/PromoCodeFormModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import BulkDeleteModal from './components/BulkDeleteModal';
import { promoCodeService } from '../../../services/promoCodeService';
import { usePermissionViewModel } from '../permissions/viewmodel/permission-viewmodel';

const PromoCodePage = () => {
  // State management
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPromoCodes, setSelectedPromoCodes] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Sorting
  const [sortField, setSortField] = useState('updatedDatetime');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Modals
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState(null);
  const [deletingPromoCode, setDeletingPromoCode] = useState(null);

  const {hasPermission} = usePermissionViewModel('code_promotionnel')


  // Fetch promo codes
  const fetchPromoCodes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await promoCodeService.getPromoCodes(
        currentPage,
        pageSize,
        searchTerm
      );
      
      setPromoCodes(response.data || []);
      setTotalItems(response.totalItems || 0);
      setTotalPages(response.totalPages || 0);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
      toast.error('Erreur lors du chargement des codes promo');
      setPromoCodes([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  // Effects
  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  // Search handler with debounce
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setCurrentPage(1);
      fetchPromoCodes();
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  // Event handlers
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Selection handlers
  const toggleSelectPromoCode = (id) => {
    setSelectedPromoCodes(prev =>
      prev.includes(id)
        ? prev.filter(promoId => promoId !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    setSelectedPromoCodes(
      selectedPromoCodes.length === promoCodes.length
        ? []
        : promoCodes.map(promo => promo.id)
    );
  };

  // Modal handlers
  const openCreateModal = () => {
    setEditingPromoCode(null);
    setIsFormModalOpen(true);
  };

  const openEditModal = (promoCode) => {
    setEditingPromoCode(promoCode);
    setIsFormModalOpen(true);
  };

  const closeFormModal = () => {
    setIsFormModalOpen(false);
    setEditingPromoCode(null);
  };

  const openDeleteModal = (promoCode) => {
    setDeletingPromoCode(promoCode);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingPromoCode(null);
  };

  const openBulkDeleteModal = () => {
    setIsBulkDeleteModalOpen(true);
  };

  const closeBulkDeleteModal = () => {
    setIsBulkDeleteModalOpen(false);
  };

  // CRUD operations
  const handleCreatePromoCode = async (promoCodeData) => {
    try {
      await promoCodeService.createPromoCode(promoCodeData);
      toast.success('Code promo créé avec succès');
      fetchPromoCodes();
    } catch (error) {
      console.error('Error creating promo code:', error);
      toast.error('Erreur lors de la création du code promo');
      throw error;
    }
  };

  const handleUpdatePromoCode = async (promoCodeData) => {
    try {
      await promoCodeService.updatePromoCode(editingPromoCode.id, promoCodeData);
      toast.success('Code promo mis à jour avec succès');
      fetchPromoCodes();
    } catch (error) {
      console.error('Error updating promo code:', error);
      toast.error('Erreur lors de la mise à jour du code promo');
      throw error;
    }
  };

  const handleFormSubmit = async (promoCodeData) => {
    if (editingPromoCode) {
      await handleUpdatePromoCode(promoCodeData);
    } else {
      await handleCreatePromoCode(promoCodeData);
    }
  };

  const handleDeletePromoCode = async () => {
    try {
      await promoCodeService.deletePromoCode(deletingPromoCode.id);
      toast.success('Code promo supprimé avec succès');
      setSelectedPromoCodes(prev => prev.filter(id => id !== deletingPromoCode.id));
      fetchPromoCodes();
      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting promo code:', error);
      toast.error('Erreur lors de la suppression du code promo');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await promoCodeService.bulkDeletePromoCodes(selectedPromoCodes);
      toast.success(`${selectedPromoCodes.length} codes promo supprimés avec succès`);
      setSelectedPromoCodes([]);
      fetchPromoCodes();
      closeBulkDeleteModal();
    } catch (error) {
      console.error('Error bulk deleting promo codes:', error);
      toast.error('Erreur lors de la suppression des codes promo');
    }
  };

  // Sort promoCodes locally if needed
  const sortedPromoCodes = React.useMemo(() => {
    if (!Array.isArray(promoCodes) || !sortField) {
      return Array.isArray(promoCodes) ? promoCodes : [];
    }
    
    return [...promoCodes].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [promoCodes, sortField, sortDirection]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Responsive */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 sm:py-6 gap-4">
            {/* Title Section */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="p-2 bg-blue-500 rounded-lg">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Codes Promotionnels</h1>
                <p className="text-sm sm:text-base text-gray-500 hidden sm:block">Gérez vos codes de réduction</p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              {selectedPromoCodes.length > 0 && (
                <button
                  onClick={openBulkDeleteModal}
                  disabled={!hasPermission('delete')}
                  className="disabled:text-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center text-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span className="hidden sm:inline">Supprimer</span>
                  <span className="sm:hidden">Supprimer ({selectedPromoCodes.length})</span>
                  <span className="hidden sm:inline ml-1">({selectedPromoCodes.length})</span>
                </button>
              )}
              
              <button
                  disabled={!hasPermission('write')}
                onClick={openCreateModal}
                className="disabled:text-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center text-sm font-medium"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                <span className="sm:hidden">Nouveau</span>
                <span className="hidden sm:inline">Nouveau Code</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Responsive */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Search and Filters - Responsive */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search Bar */}
            <div className="flex-1 max-w-none lg:max-w-xl">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Rechercher par code..."
                  className="block w-full pl-8 sm:pl-10 pr-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
            
            {/* Controls */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="border border-gray-300 rounded-lg px-3 sm:px-4 py-2 sm:py-3 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value={10}>10 par page</option>
                <option value={25}>25 par page</option>
                <option value={50}>50 par page</option>
                <option value={100}>100 par page</option>
              </select>
              
              <button
                onClick={fetchPromoCodes}
                className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center text-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline">Actualiser</span>
                <span className="sm:hidden">↻</span>
              </button>
            </div>
          </div>

          {/* Stats Row - Responsive Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-3 sm:p-4">
              <div className="flex items-center">
                <div className="p-1.5 sm:p-2 bg-blue-500 rounded-lg">
                  <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <div className="ml-2 sm:ml-4 min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-blue-600 truncate">Total</p>
                  <p className="text-lg sm:text-2xl font-bold text-blue-900">{totalItems}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-3 sm:p-4">
              <div className="flex items-center">
                <div className="p-1.5 sm:p-2 bg-green-500 rounded-lg">
                  <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-2 sm:ml-4 min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-green-600 truncate">Actifs</p>
                  <p className="text-lg sm:text-2xl font-bold text-green-900">
                    {promoCodes.filter(code => code.active).length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg p-3 sm:p-4">
              <div className="flex items-center">
                <div className="p-1.5 sm:p-2 bg-yellow-500 rounded-lg">
                  <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-2 sm:ml-4 min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-yellow-600 truncate">Sélectionnés</p>
                  <p className="text-lg sm:text-2xl font-bold text-yellow-900">{selectedPromoCodes.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-3 sm:p-4">
              <div className="flex items-center">
                <div className="p-1.5 sm:p-2 bg-purple-500 rounded-lg">
                  <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div className="ml-2 sm:ml-4 min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-purple-600 truncate">Page</p>
                  <p className="text-lg sm:text-2xl font-bold text-purple-900">{currentPage}/{totalPages}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table - Responsive */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <PromoCodeTable
            promoCodes={sortedPromoCodes}
            loading={loading}
            selectedPromoCodes={selectedPromoCodes}
            toggleSelectPromoCode={toggleSelectPromoCode}
            toggleSelectAll={toggleSelectAll}
            openEditModal={openEditModal}
            openDeleteModal={openDeleteModal}
            sortField={sortField}
            sortDirection={sortDirection}
            handleSortChange={handleSortChange}
            hasPermission={hasPermission}
          />
        </div>

        {/* Pagination - Responsive */}
        {totalPages > 1 && (
          <div className="bg-white px-4 sm:px-6 py-4 border-t border-gray-200 rounded-b-lg mt-0">
            <div className="flex items-center justify-between">
              {/* Mobile pagination */}
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <span className="px-3 py-2 text-sm text-gray-700">
                  {currentPage}/{totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
              
              {/* Desktop pagination */}
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Affichage de{' '}
                    <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span>
                    {' '}à{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * pageSize, totalItems)}
                    </span>
                    {' '}sur{' '}
                    <span className="font-medium">{totalItems}</span>
                    {' '}résultats
                  </p>
                </div>
                
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Précédent</span>
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    
                    {[...Array(totalPages)].map((_, index) => {
                      const page = index + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 2 && page <= currentPage + 2)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === currentPage
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === currentPage - 3 ||
                        page === currentPage + 3
                      ) {
                        return (
                          <span
                            key={page}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Suivant</span>
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <PromoCodeFormModal
        isOpen={isFormModalOpen}
        onClose={closeFormModal}
        onSubmit={handleFormSubmit}
        promoCode={editingPromoCode}
        isEditMode={!!editingPromoCode}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeletePromoCode}
        title="Supprimer le code promo"
        message={`Êtes-vous sûr de vouloir supprimer le code "${deletingPromoCode?.code}" ? Cette action est irréversible.`}
      />

      <BulkDeleteModal
        isOpen={isBulkDeleteModalOpen}
        onClose={closeBulkDeleteModal}
        onConfirm={handleBulkDelete}
        count={selectedPromoCodes.length}
      />
    </div>
  );
};

export default PromoCodePage;